import { AutoComplete, theme } from "antd";
import { RefSelectProps } from "antd/es/select";
import React, { useEffect, useRef, useState } from "react";
// eslint-disable-next-line import/no-internal-modules
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import { ClearIcon, JobLocationFilterRoot, LocationIcon } from "./styled";
import { JobLocationFilterProps } from "./types";

export const JobLocationFilter: React.FC<JobLocationFilterProps> = ({ queryState, queryDispatch }) => {
  const { token } = theme.useToken();

  const autocompleteInputRef = useRef<RefSelectProps | null>(null);
  const shouldSkipBlurHandling = useRef<boolean>(false);

  const [inputValue, setInputValue] = useState<string>(queryState.jobLocationAddress);

  const { getQueryPredictions, queryPredictions, placesService } = usePlacesService({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    apiKey: "AIzaSyBFKGbLYqSzHy2SzMT-4PTrAcSrVcc0ZxY",
    debounce: 200,
    // @ts-expect-error bad type
    options: {
      types: ["locality", "administrative_area_level_3", "postal_code"],
      componentRestrictions: {
        country: "US",
      },
    },
  });

  const handleBlur = () => {
    if (!shouldSkipBlurHandling.current) {
      setInputValue(queryState.jobLocationAddress);
    }
    shouldSkipBlurHandling.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if ((e.key === "Enter" || e.key === "Tab") && (e.target as HTMLInputElement).value === "") {
      clearJobLocation();
      shouldSkipBlurHandling.current = true;
      autocompleteInputRef.current?.blur();
    } else if (e.key === "Enter") {
      const dropdown = document.querySelector(".job-location-filter-dropdown");
      const activeItem = dropdown?.querySelector(".ant-select-item-option-active");

      if (!activeItem) {
        const firstPrediction = queryPredictions.at(0);
        firstPrediction &&
          handleLocationSelect(e, {
            label: extractAddressWithoutCountry(firstPrediction.description),
            value: firstPrediction.place_id ?? "",
          });
        shouldSkipBlurHandling.current = true;
        autocompleteInputRef.current?.blur();
      }
    }
  };

  const handleInputChange = (input: string) => {
    getQueryPredictions({ input });
    setInputValue(input);
  };

  const handleLocationSelect = (_v: unknown, option: { label: string; value: string }) => {
    if (!option.value || !option.label || !placesService) {
      return;
    }

    placesService.getDetails({ placeId: option.value }, (placeDetails) => {
      const location = placeDetails?.geometry?.location;
      if (location) {
        const lat = location.lat();
        const lon = location.lng();
        updateJobLocation(option.label, lat?.toString(), lon?.toString());
      }
    });
  };

  const clearJobLocation = () => {
    setInputValue("");
    queryDispatch({
      type: "update_job_location",
      value: {
        jobLocationLat: "",
        jobLocationLon: "",
        jobLocationAddress: "",
      },
    });
  };

  const updateJobLocation = (address: string, lat: string, lon: string) => {
    setInputValue(address);
    queryDispatch({
      type: "update_job_location",
      value: {
        jobLocationLat: lat,
        jobLocationLon: lon,
        jobLocationAddress: address,
      },
    });
  };

  const extractAddressWithoutCountry = (name: string) => {
    const strings = name.split(",");
    strings.pop();

    return strings.join(",");
  };

  useEffect(() => {
    setInputValue(queryState.jobLocationAddress);
  }, [queryState.jobLocationAddress]);

  return (
    <JobLocationFilterRoot id="job-location-filter" $token={token}>
      <LocationIcon $token={token} />
      <AutoComplete
        ref={autocompleteInputRef}
        allowClear={{
          clearIcon: <ClearIcon />,
        }}
        popupMatchSelectWidth={true}
        onBlur={handleBlur}
        onClear={clearJobLocation}
        onSearch={handleInputChange}
        onSelect={handleLocationSelect}
        onInputKeyDown={handleKeyDown}
        options={queryPredictions.map((qp) => ({
          label: extractAddressWithoutCountry(qp.description),
          value: qp.place_id ?? "",
        }))}
        placeholder="City, State, Zip Code"
        popupClassName="job-location-filter-dropdown"
        value={inputValue}
      />
    </JobLocationFilterRoot>
  );
};
